import { LOGO_API_KEY, TOKEN_API } from "../../constants/enviroment";
import { DEFAULT_HTTP_RETRY_OPTIONS } from "../http/types";
import { fetchWithRetryPolicy, normalizeHttpRetryOptions } from "../http/fetchWithRetryPolicy";
import { store } from "../../store/store";

import {
  type ApiAuthPreset,
  type ApiErrorParams,
  type ApiParseMode,
  type ApiRequestOptions,
  type ParsedResponse,
} from "./types";

export class ApiError extends Error {
  status: number;
  statusText: string;
  bodySnippet?: string;
  url: string;

  constructor(params: ApiErrorParams) {
    const detail = ApiError.extractDetail(params.bodySnippet);
    const message = detail
      ? `API error ${params.status}: ${detail}`
      : `API error ${params.status}: ${params.statusText || "Unknown status"}`;
    if (params.cause !== undefined) {
      super(message, { cause: params.cause });
    } else {
      super(message);
    }
    this.name = "ApiError";
    this.url = params.url;
    this.status = params.status;
    this.statusText = params.statusText;
    this.bodySnippet = params.bodySnippet;
  }

  /** Try to extract a human-readable `detail` field from a JSON body snippet (e.g. FastAPI errors). */
  private static extractDetail(bodySnippet?: string): string | null {
    if (!bodySnippet) return null;
    try {
      const parsed = JSON.parse(bodySnippet);
      if (typeof parsed?.detail === "string" && parsed.detail.length > 0) {
        return parsed.detail;
      }
    } catch {
      // bodySnippet is not valid JSON — ignore
    }
    return null;
  }
}

function getAuthHeader(auth: ApiAuthPreset): string | null {
  if (auth === "tokenApi" && TOKEN_API) {
    return `Bearer ${TOKEN_API}`;
  }
  if (auth === "logoApi" && LOGO_API_KEY) {
    return `Bearer ${LOGO_API_KEY}`;
  }
  return null;
}

function buildHeaders(
  auth: ApiAuthPreset,
  isJsonBody: boolean,
  customHeaders?: HeadersInit,
  skipActingAs?: boolean,
): Headers {
  const headers = new Headers(customHeaders);
  const authHeader = getAuthHeader(auth);

  if (authHeader && !headers.has("Authorization")) {
    headers.set("Authorization", authHeader);
  }

  if (isJsonBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  // Agency act-as: inject X-Acting-As header for backend API calls
  // actingAsAccountId = client's account_id (for backend); actingAsClientId = client's owner_id (for Supabase RLS)
  if (auth === "tokenApi" && !skipActingAs && !headers.has("X-Acting-As")) {
    const authState = store.getState().auth;
    const actAsId = authState?.actingAsAccountId ?? authState?.actingAsClientId;
    if (actAsId) {
      headers.set("X-Acting-As", actAsId);
    }
  }

  return headers;
}

function isBodyInit(body: unknown): body is BodyInit {
  if (
    typeof body === "string" ||
    body instanceof FormData ||
    body instanceof URLSearchParams ||
    body instanceof Blob ||
    body instanceof ArrayBuffer ||
    body instanceof ReadableStream ||
    ArrayBuffer.isView(body)
  ) {
    return true;
  }

  return false;
}

function normalizeBody(body: unknown, headers: Headers): BodyInit | undefined {
  if (body == null) {
    return undefined;
  }

  if (isBodyInit(body)) {
    return body;
  }

  const isJsonContentType = headers.get("Content-Type")?.includes("application/json");
  if (isJsonContentType) {
    return JSON.stringify(body);
  }

  return String(body);
}

function hasEmptyBody(response: Response): boolean {
  if (response.status === 204 || response.status === 205 || response.status === 304) {
    return true;
  }

  return response.headers.get("content-length") === "0";
}

async function parseJsonResponse<T>(response: Response): Promise<T> {
  if (hasEmptyBody(response)) {
    return null as T;
  }

  return (await response.json()) as T;
}

async function readErrorBodySnippet(response: Response): Promise<{
  bodySnippet?: string;
  readError?: unknown;
}> {
  try {
    const text = await response.text();
    return { bodySnippet: text.slice(0, 200) };
  } catch (readError) {
    return { readError };
  }
}

export async function apiRequest<T = unknown, TParse extends ApiParseMode = "json">(
  url: string,
  {
    auth = "none",
    parse = "json" as TParse,
    retry = true,
    throwOnHttpError = true,
    skipActingAs,
    method = "GET",
    headers: customHeaders,
    body,
    ...rest
  }: ApiRequestOptions<TParse> = {},
): Promise<ParsedResponse<T, TParse>> {
  const normalizedMethod = method.toUpperCase();
  const isJsonBody = body != null && !isBodyInit(body);

  const headers = buildHeaders(auth, isJsonBody, customHeaders, skipActingAs);
  const normalizedBody = normalizeBody(body, headers);
  const retryOptions = normalizeHttpRetryOptions(retry, DEFAULT_HTTP_RETRY_OPTIONS);

  const response = await fetchWithRetryPolicy(
    url,
    {
      ...rest,
      method: normalizedMethod,
      headers,
      body: normalizedBody,
    },
    retryOptions,
  );

  if (!response.ok && throwOnHttpError) {
    const { bodySnippet, readError } = await readErrorBodySnippet(response);
    throw new ApiError({
      url,
      status: response.status,
      statusText: response.statusText,
      bodySnippet,
      ...(readError !== undefined && { cause: readError }),
    });
  }

  if (parse === "raw") {
    return response as ParsedResponse<T, TParse>;
  }

  if (parse === "text") {
    const text = await response.text();
    return text as ParsedResponse<T, TParse>;
  }

  const data = await parseJsonResponse<T>(response);
  return data as ParsedResponse<T, TParse>;
}

export function apiGet<T = unknown, TParse extends ApiParseMode = "json">(
  url: string,
  options: Omit<ApiRequestOptions<TParse>, "method"> = {},
): Promise<ParsedResponse<T, TParse>> {
  return apiRequest<T, TParse>(url, { ...options, method: "GET" });
}

export function apiPost<T = unknown, TParse extends ApiParseMode = "json">(
  url: string,
  options: Omit<ApiRequestOptions<TParse>, "method"> = {},
): Promise<ParsedResponse<T, TParse>> {
  return apiRequest<T, TParse>(url, { ...options, method: "POST" });
}

export function apiPatch<T = unknown, TParse extends ApiParseMode = "json">(
  url: string,
  options: Omit<ApiRequestOptions<TParse>, "method"> = {},
): Promise<ParsedResponse<T, TParse>> {
  return apiRequest<T, TParse>(url, { ...options, method: "PATCH" });
}
